import {
  Button,
  Form,
  Input,
  Select,
  Table,
  DatePicker,
  Space,
  InputRef,
} from "antd";
import { useState, useEffect, useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { ColumnsType } from "antd/es/table";
import type { FilterDropdownProps } from "antd/es/table/interface";
import type { ColumnType } from "antd/es/table/interface";
import { EntryPackageQueryDataType } from "./types";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { Dispatch, SetStateAction } from "react";
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { ServiceTypes } from "../../../types/types";
import dayjs, { Dayjs } from "dayjs";
import ContentTitle from "../../../components/ui/ContentTitle";
import XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import * as FileSaver from "file-saver";
import "./EntryPackageQuery.scss";
import { useEntryPackageOrderListQuery } from "../../../graphql/operations/clubApp/EntryPackage/getEntryPackageOrderList";

interface EntryPackageQueryProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}
const EntryPackageQuery = ({
  setSelectedService,
  setSelectSubItem,
}: EntryPackageQueryProps) => {
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm();
  const [dataDisplay, setDataDisplay] = useState<EntryPackageQueryDataType[]>(
    [],
  );
  const [filteredData, setFilteredData] = useState<EntryPackageQueryDataType[]>(
    [],
  );
  const [showDownload, setShowDownload] = useState(false);
  const [market, setMarket] = useState("KR");
  const [showData, setShowData] = useState(false);
  const [orderStartDate, setOrderStartDate] = useState("");
  const [orderEndDate, setOrderEndDate] = useState("");
  const [orderId, setOrderId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const currentUser = useReactiveVar(__currentUser__);
  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 2,
      },
    });
  const { data } = useEntryPackageOrderListQuery({
    variables: {
      market: market,
      orderStartDate: orderStartDate,
      orderEndDate: orderEndDate,
      orderId: orderId,
      customerName: customerName,
      phoneNumber: phoneNumber,
    },
    skip: !showData,
  });

  const searchInput = useRef<InputRef>(null);
  const handleSearch = (
    selectedKeys: any,
    confirm: Function,
    dataIndex: string,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: Function) => {
    clearFilters();
    setSearchText("");
  };

  const handleSubmit = () => {
    const { orderDate, orderId, customerName, phoneNumber } =
      form.getFieldsValue();
    const startDate = orderDate ? orderDate[0].format("YYYY-MM-DD") : undefined;
    const endDate = orderDate ? orderDate[1].format("YYYY-MM-DD") : undefined;
    setOrderStartDate(startDate);
    setOrderEndDate(endDate);
    setOrderId(orderId ?? "");
    setCustomerName(customerName ?? "");
    setPhoneNumber(phoneNumber ?? "");
    setShowData(true);
  };

  const getColumnSearchProps = (dataIndex: string): ColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0] as string}
          onChange={(e) =>
            setSelectedKeys(
              e.target.value ? [e.target.value] : ([] as string[]),
            )
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText(selectedKeys[0] as string);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button type="link" size="small" onClick={close}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(value.toString().toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnDateRangeProps =
    (): ColumnType<EntryPackageQueryDataType> => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <RangePicker
            value={
              selectedKeys[0]
                ? ([
                    dayjs((selectedKeys[0] as string).split("|")[0]),
                    dayjs((selectedKeys[0] as string).split("|")[1]),
                  ] as [Dayjs, Dayjs])
                : null
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setSelectedKeys([
                  `${dates[0].format("YYYY-MM-DD")}|${dates[1].format("YYYY-MM-DD")}`,
                ]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ marginBottom: 8 }}
          />
          <Space style={{ display: "flex" }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            <Button type="link" size="small" onClick={close}>
              close
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) => {
        const [start, end] = (value as string).split("|");
        if (!record.createdAt) return false;
        const date = dayjs(record.createdAt).format("YYYY-MM-DD");
        return date >= start && date <= end;
      },
    });

  const handleChange = (value: string) => {
    setMarket(value);
  };

  const handleDownload = async () => {
    if (!filteredData || filteredData.length === 0) {
      console.error("No data to export");
      return;
    }

    const password = `abc${dayjs().format("YYYYMMDD")}`;

    const formattedData = filteredData.map(
      ({
        packageNo,
        orderId,
        createdAt,
        customerName,
        phoneNumber,
        fullAddress,
        email,
        paymentMethod,
        isAdvertisingInfoAgreed,
        isMarketingInfoCollectionAgreed,
        memberPaymentInfo,
      }) => ({
        주문번호: `${packageNo}-${orderId}`,
        주문일: dayjs(createdAt).format("YYYY-MM-DD"),
        계약자명: customerName,
        연락처: phoneNumber,
        주소: fullAddress,
        이메일: email,
        "클럽 회원비 결제등록 정보": paymentMethod,
        예금주명:
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.accountHolderName ?? "")
            : "",
        생년월일:
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.birthDate ?? "")
            : "",
        은행명:
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.bankName ?? "")
            : "",
        계좌번호:
          memberPaymentInfo?.paymentType === 1
            ? (memberPaymentInfo?.paymentNumber ?? "")
            : "",
        카드사:
          memberPaymentInfo?.paymentType === 2
            ? (memberPaymentInfo?.creditCardBankName ?? "")
            : "",
        카드번호:
          memberPaymentInfo?.paymentType === 2
            ? (memberPaymentInfo?.paymentNumber ?? "")
            : "",
        카드소유자명:
          memberPaymentInfo?.paymentType === 2
            ? (memberPaymentInfo?.accountHolderName ?? "")
            : "",
        카드유효기간:
          memberPaymentInfo?.paymentType === 2
            ? `${memberPaymentInfo?.expiryMonth ?? ""}/${memberPaymentInfo?.expiryYear ?? ""}`
            : "",
        "Optional Consent 1": isAdvertisingInfoAgreed ? "Yes" : "No",
        "Optional Consent 2": isMarketingInfoCollectionAgreed ? "Yes" : "No",
      }),
    );

    const workbook = await XlsxPopulate.fromBlankAsync();
    const sheet = workbook.sheet(0).name("Data");

    const headers = Object.keys(
      formattedData[0],
    ) as (keyof (typeof formattedData)[0])[];
    headers.forEach((header, colIdx) => {
      sheet.cell(1, colIdx + 1).value(header);
    });
    formattedData.forEach((row, rowIdx) => {
      headers.forEach((header, colIdx) => {
        sheet.cell(rowIdx + 2, colIdx + 1).value(row[header] ?? "");
      });
    });

    const buffer = await workbook.outputAsync({ password });
    const dataBlob = new Blob([buffer as ArrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    FileSaver.saveAs(
      dataBlob,
      `Entry-package-query-${dayjs().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.CLUB_APP);
    setSelectSubItem("Entry Package Query");
  }, []);
  useEffect(() => {
    if (currentUser?.afUserMarkets && currentUser?.afUserMarkets[0]?.market) {
      if (currentUser?.afUserMarkets.length === 1) {
        setMarket(currentUser?.afUserMarkets[0]?.market);
        form.setFieldValue("market", currentUser?.afUserMarkets[0]?.market);
      } else {
        setMarket("KR");
      }
    }
  }, [currentUser]);
  useEffect(() => {
    if (data?.entryPackageOrderList) {
      const list =
        (data?.entryPackageOrderList as EntryPackageQueryDataType[]) || [];
      setDataDisplay(list);
      setFilteredData(list);
    }
  }, [data]);

  const columns: ColumnsType<EntryPackageQueryDataType> = [
    {
      title: "주문번호",
      dataIndex: "orderId",
      key: "orderId",
      render: (v, i) => (v ? `${i.packageNo}-${v}` : "N/A"),
      ...getColumnSearchProps("orderId"),
    },
    {
      title: "주문일",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return a.createdAt.localeCompare(b.createdAt);
      },
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "N/A"),
      ...getColumnDateRangeProps(),
    },
    {
      title: "계약자명",
      dataIndex: "customerName",
      key: "customerName",
      render: (v, _) => (v ? v : "N/A"),
      ...getColumnSearchProps("customerName"),
    },
    {
      title: "연락처",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (v, _) => (v ? v : "N/A"),
      ...getColumnSearchProps("phoneNumber"),
    },
    {
      title: "주소",
      dataIndex: "fullAddress",
      key: "fullAddress",
      render: (v, _) => (v ? v : "N/A"),
    },
    {
      title: "이메일",
      dataIndex: "email",
      key: "email",
      render: (v, _) => (v ? v : "N/A"),
      ...getColumnSearchProps("email"),
    },
    {
      title: "클럽 회원비 결제등록 정보",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (v, _) => (v ? v.replace("_", " ") : "N/A"),
    },
    {
      title: "예금주명",
      dataIndex: ["memberPaymentInfo", "accountHolderName"],
      key: "accountHolderName",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
    },
    {
      title: "생년월일",
      dataIndex: ["memberPaymentInfo", "birthDate"],
      key: "birthDate",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
    },
    {
      title: "은행명",
      dataIndex: ["memberPaymentInfo", "bankName"],
      key: "bankName",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
    },
    {
      title: "계좌번호",
      dataIndex: ["memberPaymentInfo", "displayMask"],
      key: "displayMaskBank",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 1 ? v : "N/A",
    },
    {
      title: "카드사",
      dataIndex: ["memberPaymentInfo", "creditCardBankName"],
      key: "creditCardBankName",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
    },
    {
      title: "카드번호",
      dataIndex: ["memberPaymentInfo", "displayMask"],
      key: "displayMaskCard",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
    },
    {
      title: "카드소유자명",
      dataIndex: ["memberPaymentInfo", "accountHolderName"],
      key: "cardHolderName",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
    },
    {
      title: "생년월일",
      dataIndex: ["memberPaymentInfo", "birthDate"],
      key: "cardBirthDate",
      render: (v, i) =>
        v && i.memberPaymentInfo?.paymentType === 2 ? v : "N/A",
    },
    {
      title: "카드유효기간",
      dataIndex: ["memberPaymentInfo", "expiryMonth"],
      key: "creditCardExpirationDate",
      render: (_, i) =>
        i.memberPaymentInfo?.paymentType === 2
          ? `${i.memberPaymentInfo.expiryMonth}/${i.memberPaymentInfo.expiryYear}`
          : "N/A",
    },
  ];

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Entry Package Query",
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="entryPackageQueryWrapper">
      <ContentTitle title="Entry Package Query" />
      <div className="table-container">
        {currentUser?.afUserMarkets?.length &&
          currentUser?.afUserMarkets?.length > 1 && (
            <div className="flex justify-end p-5">
              <div>
                <span>Market:</span>
                <Select
                  onChange={handleChange}
                  defaultValue={market}
                  options={[
                    {
                      value: "TW",
                      label: "TW",
                    },
                    {
                      value: "HK",
                      label: "HK",
                    },
                    {
                      value: "KR",
                      label: "KR",
                    },
                  ]}
                />
              </div>
            </div>
          )}
        <Form
          form={form}
          name="entry-package-query-form"
          className="flex flex-wrap gap-10"
        >
          <Form.Item label="Order Date" name="orderDate" className="w-4/12">
            <RangePicker />
          </Form.Item>
          <Form.Item label="Order ID" name="orderId" className="w-4/12">
            <Input />
          </Form.Item>
          <Form.Item
            label="Customer Name"
            name="customerName"
            className="w-4/12"
          >
            <Input />
          </Form.Item>
          <Form.Item label="Phone Number" name="phoneNumber" className="w-4/12">
            <Input />
          </Form.Item>

          <div className="flex w-full">
            <Form.Item shouldUpdate noStyle>
              {() => (
                <Button
                  type="primary"
                  onClick={() => {
                    form
                      .validateFields()
                      .then(() => {
                        handleSubmit();
                      })
                      .catch((e) => console.log(e));
                    setShowDownload(true);
                  }}
                >
                  Query
                </Button>
              )}
            </Form.Item>
            <Button
              className="ms-2"
              onClick={() => {
                form.resetFields();
                setOrderStartDate("");
                setOrderEndDate("");
                setOrderId("");
                setCustomerName("");
                setPhoneNumber("");
                setShowData(false);
                setShowDownload(false);
              }}
            >
              Clear
            </Button>
            {dataDisplay.length > 0 && showDownload && (
              <Button className="ms-2" onClick={handleDownload}>
                Download
              </Button>
            )}
          </div>
        </Form>
      </div>
      {showData && (
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={dataDisplay}
            scroll={{ x: "max-content" }}
            onChange={(_, __, ___, extra) => {
              setFilteredData(extra.currentDataSource);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EntryPackageQuery;
